import httpClient from './httpClient';

// 환경 변수에서 API URL 가져오기
const MANAGER_ENDPOINT = `/order`;

/**
 * 통합 주문 목록을 조회합니다
 * 스키마: api-requirement.md의 UnifiedOrder
 * @param {{userid?: string|number}} params
 */
export const fetchOrdersByUser = async (params = {}) => {
  try {
    const response = await httpClient.get(`${MANAGER_ENDPOINT}/check`, { params });
    return response.data;
  } catch (error) {
    console.error('주문 목록 조회 중 오류 발생:', error);
    throw error;
  }
};

/**
 * 상품 메타(참고 URL, 특이사항 메모) 저장 (userid는 세션 등으로 서버에서 처리)
 * @param {{productid:number|string, url?:string, memo?:string}} payload
 */
export const saveProductMeta = async (payload) => {
  try {
    const response = await httpClient.post(`${MANAGER_ENDPOINT}/check/product-meta`, payload);
    return response.data;
  } catch (error) {
    console.error('상품 메타 저장 중 오류 발생:', error);
    throw error;
  }
};

/**
 * 주문을 취소합니다.
 * @param {{markerNumber:number|string, productOrderIds:Array<string>, code:string, cancelDetailedReason?:string|null}} payload
 */
export const cancelOrder = async (payload) => {
  try {
    const response = await httpClient.post(`${MANAGER_ENDPOINT}/cancel`, payload);
    return response.data;
  } catch (error) {
    console.error('주문 취소 중 오류 발생:', error);
    throw error;
  }
};

/**
 * 퀵스타 액셀 대량등록용 데이터 저장
 * @param {*} payload
 */
export const saveQuickstarExcelData = async (payload) => {
  try {
    const response = await httpClient.post(`${MANAGER_ENDPOINT}/quickstar`, payload);
    return response.data;
  } catch (error) {
    console.error('퀵스타 액셀 데이터 저장 중 오류 발생:', error);
    throw error;
  }
};

/**
 * 퀵스타 액셀 업로드 데이터로 그룹번호/운송장/전화번호 일괄 업데이트
 * @param {{rows:Array<{groupNumber:string,trackingNumber:string,phoneNumber:string}>}} payload
 */
export const updateQuickstarTrackingByExcel = async (payload) => {
  try {
    const response = await httpClient.post(`${MANAGER_ENDPOINT}/quickstar/tracking`, payload);
    return response.data;
  } catch (error) {
    console.error('퀵스타 운송장 업데이트 중 오류 발생:', error);
    throw error;
  }
};

/**
 * 퀵스타 액셀(.xls) 다운로드 + 서버 데이터 비우기
 */
export const downloadQuickstarExcel = async () => {
  try {
    const response = await httpClient.get(`${MANAGER_ENDPOINT}/quickstar`, {
      responseType: 'blob'
    });

    const disposition = response?.headers?.['content-disposition'] || '';
    const encodedName = disposition.match(/filename\*=UTF-8''([^;]+)/i)?.[1];
    const plainName = disposition.match(/filename="?([^"]+)"?/i)?.[1];
    const filename = encodedName ? decodeURIComponent(encodedName) : (plainName || `quickstar_${Date.now()}.xls`);

    return {
      blob: response.data,
      filename,
      rowCount: Number(response?.headers?.['x-quickstar-row-count'] || 0),
      deletedCount: Number(response?.headers?.['x-quickstar-deleted-count'] || 0),
    };
  } catch (error) {
    console.error('퀵스타 액셀 다운로드 중 오류 발생:', error);
    throw error;
  }
};

/**
 * 주문 메시지 목록을 조회합니다
 * @param {{page?: number, size?: number, phone?: string}} params
 */
export const getOrderMessages = async (params = {}) => {
  try {
    const response = await httpClient.get(`${MANAGER_ENDPOINT}/message/`, {
      params
    });
    return response.data;
  } catch (error) {
    console.error('주문 메시지 목록 조회 중 오류 발생:', error);
    throw error;
  }
};

/**
 * 주문 메시지를 생성합니다
 * @param {*} data
 */
export const createOrderMessage = async (data) => {
  try {
    const response = await httpClient.post(`${MANAGER_ENDPOINT}/message/`, data);
    return response.data;
  } catch (error) {
    console.error('주문 메시지 생성 중 오류 발생:', error);
    throw error;
  }
};

/**
 * 주문 메시지를 수정합니다
 * @param {number|string} id
 * @param {*} data
 */
export const updateOrderMessage = async (id, data) => {
  try {
    const response = await httpClient.put(`${MANAGER_ENDPOINT}/message/${id}`, data);
    return response.data;
  } catch (error) {
    console.error('주문 메시지 수정 중 오류 발생:', error);
    throw error;
  }
};

/**
 * 주문 메시지를 발송합니다 (알리고 연동)
 * @param {number|string} id
 * @param {{type:string,recipient_phone:string,product_name?:string,courier?:string,tracking_number?:string,image_url_1?:string,image_url_2?:string,title?:string}} data
 */
export const sendOrderMessage = async (id, data) => {
  try {
    const response = await httpClient.post(`${MANAGER_ENDPOINT}/message/${id}/send`, data);
    return response.data;
  } catch (error) {
    console.error('주문 메시지 발송 중 오류 발생:', error);
    throw error;
  }
};


/**
 * 주문 메시지를 삭제합니다
 * @param {number|string} id
 */
export const deleteOrderMessage = async (id) => {
  try {
    const response = await httpClient.delete(`${MANAGER_ENDPOINT}/message/${id}`);
    return response.data;
  } catch (error) {
    console.error('주문 메시지 삭제 중 오류 발생:', error);
    throw error;
  }
};

/**
 * 운송장 상태를 조회합니다
 * @param {string} trackingNumber
 * @param {string} kyungdongTrackingNumber
 */
export const fetchTrackingStatus = async (trackingNumber, kyungdongTrackingNumber) => {
  if (!trackingNumber) {
    throw new Error('운송장 번호를 입력하세요.');
  }
  try {
    const encodedTrackingNumber = encodeURIComponent(trackingNumber);
    const kdQuery = kyungdongTrackingNumber ? `?kd=${encodeURIComponent(kyungdongTrackingNumber)}` : '';
    const response = await httpClient.get(`${MANAGER_ENDPOINT}/message/tracking/${encodedTrackingNumber}${kdQuery}`);
    return response.data;
  } catch (error) {
    console.error('운송장 상태 조회 중 오류 발생:', error);
    throw error;
  }
};

/**
 * 정산 데이터 조회 (페이지네이션, 필터링)
 * @param {{productName?: string, recentDays?: number, page?: number, pageSize?: number}} params
 */
export const fetchSettlementData = async (params = {}) => {
  try {
    const response = await httpClient.get(`${MANAGER_ENDPOINT}/settlement`, { params });
    return response.data;
  } catch (error) {
    console.error('정산 데이터 조회 중 오류 발생:', error);
    throw error;
  }
};

/**
 * 정산 일별 통계 조회
 * @param {{days: number}} params
 */
export const fetchSettlementDailyStats = async (params = {}) => {
  try {
    const response = await httpClient.get(`${MANAGER_ENDPOINT}/settlement/daily-stats`, { params });
    return response.data;
  } catch (error) {
    console.error('정산 일별 통계 조회 중 오류 발생:', error);
    throw error;
  }
};

/**
 * 정산 장부 엑셀 다운로드
 * @param {{startDate: string, endDate: string}} params
 */
export const exportLedger = async ({ startDate, endDate }) => {
  try {
    const response = await httpClient.get(`${MANAGER_ENDPOINT}/settlement/ledger/export`, {
      params: { startDate, endDate },
      responseType: 'blob'
    });
    return response;
  } catch (error) {
    console.error('장부 출력 다운로드 실패:', error);
    throw error;
  }
};

/**
 * 정산 데이터 삭제
 * @param {string} orderNumber
 */
export const deleteSettlementData = async (orderNumber) => {
  try {
    const response = await httpClient.delete(`${MANAGER_ENDPOINT}/settlement/${orderNumber}`);
    return response.data;
  } catch (error) {
    console.error('정산 데이터 삭제 중 오류 발생:', error);
    throw error;
  }
};

/**
 * 정산 데이터 직접 수정 (expectedSettlement, purchaseAmountCny, actualDeliveryFee)
 * @param {string} orderNumber
 * @param {{expectedSettlement?: number, purchaseAmountCny?: number, actualDeliveryFee?: number}} payload
 */
export const patchSettlementData = async (orderNumber, payload = {}) => {
  try {
    const response = await httpClient.patch(`${MANAGER_ENDPOINT}/settlement/${orderNumber}`, payload);
    return response.data;
  } catch (error) {
    console.error('정산 데이터 직접 수정 중 오류 발생:', error);
    throw error;
  }
};

/**
 * 정산 상품 검색
 * @param {{productName: string, limit?: number}} params
 */
export const searchSettlementProducts = async (params = {}) => {
  try {
    const response = await httpClient.get(`${MANAGER_ENDPOINT}/settlement/search`, { params });
    return response.data;
  } catch (error) {
    console.error('정산 상품 검색 중 오류 발생:', error);
    throw error;
  }
};